import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, InputAdornment, CircularProgress, Button } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import { Search, Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import DetailDialog from './DetailDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { formatDate } from '../lib/dateUtils';

interface GenericTablePageProps {
  tableName: string;
  title: string;
  columns?: GridColDef[];
  fields?: Array<{
    name: string;
    label: string;
    type?: 'text' | 'email' | 'number' | 'date' | 'textarea' | 'richtext' | 'select' | 'boolean' | 'multiselect' | 'currency' | 'targets' | 'image' | 'file';
    required?: boolean;
    disabled?: boolean;
    multiline?: boolean;
    width?: 'full' | 'half' | 'third';
    bucket?: string;
    accept?: string;
    options?: Array<{ value: any; label: string }>;
    foreignKey?: {
      table: string;
      valueField: string;
      labelField: string;
    };
    targets?: Array<{
      type: string;
      label: string;
      table: string;
      valueField: string;
      labelField: string;
    }>;
  }>;
  selectQuery?: string; // For complex joins
  // When set, adds a "View" action + row double-click that navigates to a
  // read-only detail page (e.g. `(id) => \`/tenders/${id}\`` -> EntityDetailPage).
  detailPath?: (id: string | number) => string;
  // When set, Edit/Add navigate to a full-page form instead of opening the
  // popup dialog (for entities with too many fields for a cramped modal).
  // `id === null` means "add new".
  editPath?: (id: string | number | null) => string;
}

const GenericTablePage: React.FC<GenericTablePageProps> = ({ tableName, title, columns, fields, selectQuery, detailPath, editPath }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [autoColumns, setAutoColumns] = useState<GridColDef[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | number | null>(null);
  const [autoFields, setAutoFields] = useState<any[]>([]);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const query = selectQuery || '*';
      const { data: tableData, error } = await supabase
        .from(tableName)
        .select(query)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setData(tableData || []);
      
      // Auto-generate columns and fields if not provided
      if (!columns && tableData && tableData.length > 0) {
        const keys = Object.keys(tableData[0]);
        // Filter out id, created_at, updated_at from display
        const filteredKeys = keys.filter(key => 
          key !== 'id' && 
          key !== 'created_at' && 
          key !== 'updated_at'
        );
        
        const generatedColumns: GridColDef[] = filteredKeys.map((key) => {
          const isDateField = key.includes('date') || key.includes('_at') || key.endsWith('Date');
          
          return {
            field: key,
            headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            width: isDateField ? 150 : 150,
            flex: isDateField ? undefined : 1,
            // Add date formatter for date fields
            ...(isDateField && {
              valueFormatter: (value: string | Date | null | undefined) => formatDate(value, i18n.language === 'ar' ? 'ar' : 'en', 'medium'),
            }),
          };
        });
        
        // Add actions column
        generatedColumns.push({
          field: 'actions',
          type: 'actions',
          headerName: t('table.actions'),
          width: detailPath ? 140 : 100,
          getActions: (params) => [
            ...(detailPath ? [
              <GridActionsCellItem
                icon={<Visibility />}
                label={t('table.view')}
                onClick={() => navigate(detailPath(params.id))}
              />,
            ] : []),
            <GridActionsCellItem
              icon={<Edit />}
              label={t('table.edit')}
              onClick={() => handleEdit(params.id)}
            />,
            <GridActionsCellItem
              icon={<Delete />}
              label={t('table.delete')}
              onClick={() => handleDelete(params.id)}
            />,
          ],
        });

        setAutoColumns(generatedColumns);
        
        // Generate fields for form
        const generatedFields = keys
          .filter(key => key !== 'id' && key !== 'created_at' && key !== 'updated_at')
          .map(key => ({
            name: key,
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            type: key.includes('email') ? 'email' as const : 
                  key.includes('date') ? 'date' as const : 
                  'text' as const,
          }));
        setAutoFields(generatedFields);
      }
    } catch (error) {
      console.error(`Error fetching ${tableName}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (editPath) { navigate(editPath(null)); return; }
    setSelectedRecordId(null);
    setDetailDialogOpen(true);
  };

  const handleEdit = (id: string | number) => {
    if (editPath) { navigate(editPath(id)); return; }
    setSelectedRecordId(id);
    setDetailDialogOpen(true);
  };

  const handleDelete = (id: string | number) => {
    setSelectedRecordId(id);
    setDeleteDialogOpen(true);
  };

  // Add actions column to custom columns if not present
  const displayColumns = React.useMemo(() => {
    const cols = columns || autoColumns;
    const hasActions = cols.some(col => col.field === 'actions');
    
    if (!hasActions) {
      return [
        ...cols,
        {
          field: 'actions',
          type: 'actions' as const,
          headerName: t('table.actions'),
          width: detailPath ? 140 : 100,
          getActions: (params: any) => [
            ...(detailPath ? [
              <GridActionsCellItem
                key="view"
                icon={<Visibility />}
                label={t('table.view')}
                onClick={() => navigate(detailPath(params.id))}
              />,
            ] : []),
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label={t('table.edit')}
              onClick={() => handleEdit(params.id)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label={t('table.delete')}
              onClick={() => handleDelete(params.id)}
            />,
          ],
        },
      ];
    }
    return cols;
  }, [columns, autoColumns, t, detailPath, editPath, navigate]);
  
  const displayFields = fields || autoFields;

  const filteredData = data.filter((item: any) =>
    Object.values(item).some((value) =>
      value !== null && value !== undefined &&
      String(value).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{title}</Typography>
        <Button variant="contained" sx={{ gap: 1 }} onClick={handleAdd} size="small">
          <Add sx={{ fontSize: 18 }} />{t('table.add')}
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder={t('table.search')}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 2 }}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box sx={{ height: 600, width: '100%' }}>
        {loading && displayColumns.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <DataGrid
            rows={filteredData}
            columns={displayColumns}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => (detailPath ? navigate(detailPath(params.id)) : handleEdit(params.id))}
            density="compact"
            localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: '0.8125rem',
              },
              '& .MuiDataGrid-columnHeader': {
                fontSize: '0.75rem',
              },
            }}
          />
        )}
      </Box>

      <DetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        tableName={tableName}
        recordId={selectedRecordId || undefined}
        onSave={fetchData}
        fields={displayFields}
      />

      {selectedRecordId && (
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          tableName={tableName}
          recordId={selectedRecordId}
          onDelete={fetchData}
        />
      )}
    </Box>
  );
};

export default GenericTablePage;
